<template>
  <div v-if="$root.ready && $store.state.currencies.length" id="app">
    <Menu />
    <router-view />
    <div></div>
    <PremiumButton v-if="showPremiumButton" />
    <Footer />
  </div>
</template>

<script>
import gql from "graphql-tag";
import "vue-select/src/scss/vue-select.scss";
import { ACTIONS } from "./store";
import filigranaBackground from "@/assets/filigrana-background.png";
import Menu from "@/components/Menu/Menu.vue";
import { applyBrandingForUser } from "@/helpers/branding";


const ACCOUNT_CHAIN_QUERY = gql`
  query AccountChain {
    accountChain
  }
`;

const RESOLVE_PARTNER_QUERY = gql`
  query ResolvePartner($slug: String) {
    resolvePartner(slug: $slug) {
      userId
    }
  }
`;

export default {
  apollo: {
    currencys: {
      query: gql`
        query Currencies {
          currencys(query: "", limit: 100, page: 1) {
            rows {
              id
              code
              usdPrice
              markup
              createdAt
            }
            count
          }
        }
      `,
      fetchPolicy: "cache-and-network",
      update: data => {
        window.app.$store.dispatch(ACTIONS.SET_CURRENCIES, data.currencys.rows);
      }
    }
  },
  components: {
    Menu,
    Footer: () =>
      import(/* webpackChunkName: "footer" */ "@/components/Footer/Footer.vue"),
    PremiumButton: () =>
      import(/* webpackChunkName: "premium-button" */ "@/components/PremiumButton/PremiumButton.vue")
  },
  mounted() {
    // El store se hidrata desde localStorage antes de montar, asi que pinta
    // con lo ultimo que sabiamos y evita el flash de marca Cardinal.
    this.refreshBranding();
    this.$nextTick(() => {
      this.updateBodyBackground();
      this.applyBotmakerVisibility(this.$route);

      if (!this.shouldHideBotmaker(this.$route)) {
        this.loadBotmaker();
      }
    });
    this.$router.onReady(() => this.initPartner()); // wait for initial route to resolve
  },
  watch: {
    '$route'(route) {
      this.updateBodyBackground();
      this.applyBotmakerVisibility(route);
    },
    // Cubre login (/login/:data), logout y expiracion de token: los tres
    // pasan por SET_LOGIN, que es lo unico que toca user.email.
    "$store.state.user.email"() {
      this.refreshBranding();
    }
  },
  methods: {
    /**
     * Pinta el branding con lo que ya tenemos en el store y, si hace falta,
     * refresca la cadena de cuentas contra la API y vuelve a pintar.
     *
     * El orden importa: primero pinta con la cadena cacheada en localStorage
     * para que no haya flash de marca Cardinal, y despues corrige si el
     * servidor devuelve algo distinto (por ejemplo si movieron al usuario de
     * cuenta madre desde el backoffice).
     */
    async refreshBranding() {
      applyBrandingForUser(this.$store.state.user);
      await this.loadAccountChain();
      applyBrandingForUser(this.$store.state.user);
    },

    /**
     * Trae `accountChain` de la API. Silencioso a proposito: si la query no
     * existe todavia en el server desplegado, o el token expiro, el branding
     * cae al email propio y la app sigue andando.
     */
    async loadAccountChain() {
      const user = this.$store.state.user;
      if (!user || !user.isLogged || !user.token) return;
      try {
        const result = await this.$apollo.query({
          query: ACCOUNT_CHAIN_QUERY,
          fetchPolicy: 'no-cache'
        });
        const chain = result.data && result.data.accountChain;
        // La sesion puede haber terminado mientras la query estaba en vuelo:
        // sin este chequeo el logout reescribia `cardinal-user` con la cadena
        // y el branding sobrevivia a la recarga.
        if (Array.isArray(chain) && this.$store.state.user.isLogged) {
          this.$store.dispatch(ACTIONS.SET_ACCOUNT_CHAIN, chain);
        }
      } catch (e) {
        console.warn('[branding] no se pudo resolver accountChain:', e);
      }
    },

    shouldHideBotmaker(route) {
      const name = route && route.name ? String(route.name) : "";
      // ocultar en checkout
      return name === "Checkout";
    },

    applyBotmakerVisibility(route) {
      const hide = this.shouldHideBotmaker(route);

      // Si webchat ya cargó, usar API oficial
      if (typeof window.bmHide === "function" && typeof window.bmShow === "function") {
        if (hide) {
          window.bmHide();
        } else {
          window.bmShow();
          // mantener minimizado al volver a mostrar
          if (typeof window.bmMinimize === "function") window.bmMinimize();
        }
      }
    },

    loadBotmaker() {
      if (window.botmakerLoaded) return;

      window.BOTMAKER_ACTION = {
        onload: () => {
          this.applyBotmakerVisibility(this.$route);
        }
      };

      const script = document.createElement("script");
      script.type = "text/javascript";
      script.async = true;
      script.src = "https://go.botmaker.com/rest/webchat/p/WFRPQIHPEJ/init.js";
      document.body.appendChild(script);

      window.botmakerLoaded = true;
    },

    async initPartner() {
      const slug = this.$route.query.partner;
      if (!slug || typeof slug !== 'string') return;
      try {
        const result = await this.$apollo.query({
          query: RESOLVE_PARTNER_QUERY,
          variables: { slug },
          fetchPolicy: 'no-cache'
        });
        const userId = result.data && result.data.resolvePartner && result.data.resolvePartner.userId;
        if (userId) {
          sessionStorage.setItem('wl_partner_slug', slug);
          sessionStorage.setItem('wl_partner_user_id', String(userId));
          this.$root.partnerSlug = slug;
        }
      } catch (e) {
        console.warn('[whitelabel] initPartner failed, reservation will be anonymous:', e);
      }
    },

    updateBodyBackground() {
      const body = document.body;
      body.style.setProperty('background-image', `url("${filigranaBackground}")`, 'important');
      body.style.setProperty('background-color', '#fafcfe', 'important');
      body.style.setProperty('background-repeat', 'repeat', 'important');
      body.style.setProperty('background-size', 'auto', 'important');
      body.style.setProperty('background-attachment', 'fixed', 'important');
      document.documentElement.style.removeProperty('--client-bg-color');
      document.documentElement.style.removeProperty('--client-bg-image');
    }
  },
  computed: {
    isClientMode() {
      return !!this.$route.params.clientName;
    },
    showPremiumButton() {
      return !this.isClientMode && !this.shouldHideBotmaker(this.$route);
    },
    currentClient() {
      return this.$route.params.clientName;
    },
    clientBackgroundStyle() {
      if (this.currentClient === 'comprandoviajes') {
        return {
          backgroundImage: 'url("/assets/clientes/comprandoviajes/noise.webp")',
          backgroundColor: '#EBE9DE'
        };
      }
      return {
        backgroundImage: 'url("./assets/filigrana-background.png")',
        backgroundColor: 'inherit'
      };
    }
  }
};
</script>

<style lang="scss">

:root {
  --font: #243746;
  --blue: #0055b8;
  --lblue: #8a939d;
  --bgbody: #fafcfe;
  --bgtitle: #f0f4fa;
  --bg-title: #edf3fa;
  --lighter-blue: #7ca8db;
  --light-gray: #e5e7e9;
  --verylight-gray: #fcfcfc;
  --dark-gray: #353b48;
  --green: #00af9a;
  --cta: #00af9a;
  --lightsteelblue: #a0c5d4;
  --pink: #e7004c;
  --white: #ffffff;
  --light-silver: #f6f7fa;
  --tag: rgba(229, 231, 233, 0.5);
  --main-font: "Roboto";
  --alt-font: "Calibri";
  --orange: #f18a00;
  --light-orange: #efb000;
  --checkbox: #c4c9ce;
  --dp-back: #e5f7fd;
  --shadow: 0 20px 50px 0 rgba(0, 46, 109, 0.1);
  --black-hertz: #283444;
  --yellow-hertz: #fecf0f;
  --red-dollar: #d00c24;
  --blue-thrifty: #007ac3;
  --header-h: 66px;

  // Superficies del listado de resultados.
  --listing-max: 1616px;
  --filter-bg: #0d3c72;
  --hairline: #e6ebf2;
  --ink-2: #5b6b7c;
  --soft-shadow: 0 1px 2px rgba(16, 42, 67, 0.04), 0 8px 24px rgba(16, 42, 67, 0.07);
  --soft-shadow-hi: 0 2px 4px rgba(16, 42, 67, 0.06), 0 16px 40px rgba(16, 42, 67, 0.12);
}

// Cuenta con marca propia: los CTA toman el color de marca en lugar del verde.
html.branded {
  --cta: var(--blue);
}


@font-face {
  font-family: "Calibri";
  font-style: normal;
  font-weight: 700;
  font-display: swap;
  src: url("/fonts/calibri_bold.woff2") format("woff2");
}

@font-face {
  font-family: "Calibri";
  font-style: normal;
  font-weight: 300;
  font-display: swap;
  src: url("/fonts/calibri_light.woff2") format("woff2");
}

@font-face {
  font-family: "Calibri";
  font-style: normal;
  font-weight: 400;
  font-display: swap;
  src: url("/fonts/calibri_regular.woff2") format("woff2");
}

* {
  font-family: Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  outline: transparent;
}

body {
  /* El fondo ahora se setea dinamicamente */
  color: var(--font);
  margin: 0;
  padding: 0;
  background-repeat: repeat;
  background-size: auto;
  background-attachment: fixed;
  #app {
    overflow-x: clip;
    min-height: 100vh;
    padding-top: var(--header-h);
    background: transparent; 
    .container {
      width: 100%;
      max-width: 1250px;
      margin: 0 auto;
      @media screen and (max-width: 768px) {
        width: calc(100% - 40px);
      }
    }
    // El listado de resultados usa un contenedor mas ancho para que la
    // grilla de autos no quede con tanto aire a los costados. La excepcion
    // vive aca porque la regla base la gana por el #app.
    .listing .container {
      max-width: var(--listing-max);
    }
    // SearchBar trae su propio .container anidado: se estira al del padre
    // en lugar de aplicar un segundo max-width.
    .listing .container.slim-bar {
      max-width: 100%;
    }
    a {
      &:hover {
        opacity: 0.8;
      }
    }
  }
}

.row {
  display: flex;
  flex-flow: row wrap;
  margin-left: -15px;
  margin-right: -15px;
}

.h4-title {
  font-size: 16px;
  color: var(--dark-gray);
  font-weight: bold;
}

.h3-title {
  font-family: var(--alt-font);
  font-size: 32px;
  line-height: 32px;
  @media (max-width: 767px) {
    line-height: 40px !important;
  }
}

.margin-text {
  margin: 0 0 1rem;
}

.vue-slider-process {
  background-color: var(--pink);
}

/* Title: Contact, About page */
.title-content {
  background-color: var(--bgtitle);
  height: 168px;
  padding: 40px 20px;
  box-sizing: border-box;
  h2 {
    font-family: var(--alt-font);
    font-size: 48px;
    line-height: 48px;
    color: var(--white);
    margin: 0;
    @media (max-width: 991px) {
      font-size: 32px;
      line-height: 32px;
    }
  }
  @media (max-width: 991px) {
    padding: 36px 20px;
    height: 154px;
    h2 {
      font-size: 32px;
      line-height: 32px;
    }
  }
}

/* Checkbox */
.checkbox-container {
  color: var(--dark-gray);
  display: block;
  position: relative;
  padding-left: 28px;
  margin-bottom: 12px;
  cursor: pointer;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
    &:checked ~ .checkmark {
      background-color: var(--blue);
      border-color: var(--blue);
      &:after {
        display: block;
      }
    }
  }
  .checkmark {
    position: absolute;
    top: 0;
    left: 0;
    height: 16px;
    width: 16px;
    background-color: var(--white);
    border-radius: 4px;
    border: 1px solid var(--lblue);
    @media (max-width: 1199px) {
      height: 24px;
      width: 24px;
      top: 0px;
    }
    &:after {
      content: "";
      position: absolute;
      display: none;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      background-image: url("data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyB3aWR0aD0iMTJweCIgaGVpZ2h0PSI5cHgiIHZpZXdCb3g9IjAgMCAxMiA5IiB2ZXJzaW9uPSIxLjEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiPgogICAgPCEtLSBHZW5lcmF0b3I6IFNrZXRjaCA2My4xICg5MjQ1MikgLSBodHRwczovL3NrZXRjaC5jb20gLS0+CiAgICA8dGl0bGU+UGF0aDwvdGl0bGU+CiAgICA8ZGVzYz5DcmVhdGVkIHdpdGggU2tldGNoLjwvZGVzYz4KICAgIDxnIGlkPSJTeW1ib2xzIiBzdHJva2U9Im5vbmUiIHN0cm9rZS13aWR0aD0iMSIgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiPgogICAgICAgIDxnIGlkPSJjaGVjay1ib3giIHRyYW5zZm9ybT0idHJhbnNsYXRlKC00LjAwMDAwMCwgLTUuMDAwMDAwKSIgc3Ryb2tlPSIjRkZGRkZGIiBzdHJva2Utd2lkdGg9IjIiPgogICAgICAgICAgICA8ZyBpZD0iUGF0aCI+CiAgICAgICAgICAgICAgICA8cG9seWxpbmUgcG9pbnRzPSIxNC43MzQzNDk5IDYuNjY2NjY2NjcgOC45NjAxNzY5MSAxMi42NzQ2NDQ4IDUuODMzMzMzMzMgOS42ODU4NDQ5MSI+PC9wb2x5bGluZT4KICAgICAgICAgICAgPC9nPgogICAgICAgIDwvZz4KICAgIDwvZz4KPC9zdmc+");
      background-repeat: no-repeat;
      background-position: center center;
    }
  }
}

/* Radio */
.radio-container {
  color: var(--dark-gray);
  display: block;
  position: relative;
  padding-left: 36px;
  margin-bottom: 12px;
  cursor: pointer;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
    &:checked ~ .checkmark {
      background-color: var(--blue);
      border-color: var(--blue);
      &:after {
        display: block;
      }
    }
  }
  .checkmark, .checkmark-Hertz, .checkmark-Dollar, .checkmark-Thrifty {
    position: absolute;
    top: 0;
    left: 0;
    height: 24px;
    width: 24px;
    background-color: var(--white);
    border-radius: 100%;
    border: 1px solid var(--lblue);
    &:after {
      content: "";
      position: absolute;
      display: none;
      left: 0px;
      top: 0px;
      width: calc(100% - 10px);
      height: calc(100% - 10px);
      border: 5px solid white;
      border-radius: 100%;
    }
  }
}

/* Switch */
.switch {
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  width: 144px;
  height: 48px;
  border-radius: 8px;
  border: 1px solid var(--light-gray);
  padding: 12px 18px;
  background: var(--light-gray);
  color: var(--lblue);
  font-family: Calibri;
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  line-height: 20px;
  position: relative;
  cursor: pointer;
  text-transform: uppercase;
  & > span.switcher {
    box-sizing: border-box;
    border-radius: 8px;
    border: 1px solid var(--light-gray);
    background: white;
    color: var(--font);
    width: 72px;
    height: 48px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    left: 0;
    z-index: 1;
    cursor: pointer;
    text-transform: uppercase;
    transition: transform 0.5s ease-in-out;
    &.active {
      transform: translateX(100%);
    }
  }
}

.custom-vue-select {
  cursor: pointer;
  #vs1__combobox {
    box-sizing: border-box;
    background: var(--white);
    border: none;
    border: 1px solid #e5e7e9;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 86px;
    width: 100%;
    height: 50px;
    padding: 0;
    & .vs__selected-options {
      align-items: center;
      & .vs__selected {
        color: var(--font);
        opacity: 1;
      }
      & .vs__search {
        display: none;
      }
    }
    & .vs__actions {
      & .vs__clear {
        display: none;
      }
    }
  }
  #vs1__listbox {
    transform: translateX(0px);
    min-width: 86px;
    max-width: 86px;
    padding: 0;
    & > li {
      height: 40px;
      display: flex;
      align-items: center;
      padding: 0;
      padding: 12px 9px;
      &:hover,
      &.vs__dropdown-option--highlight {
        background: var(--blue);
      }
    }
  }
  .vs__clear {
    display: none;
  }
  .vs__open-indicator {
    fill: var(--blue);
  }
}

/* Form select */
form {
  & ::v-deep .form-vue-select {
    display: block;
    border: 1px solid var(--lblue);
    border-radius: 4px;
    margin-top: 8px;
    height: 48px;
    width: 100%;
    box-sizing: border-box;
    .vs__dropdown-toggle {
      border: 0;
      height: 46px;
      padding: 0;
    }
    .vs {
      &__actions {
        padding: 1px 10px 0 3px;
      }
      &__search {
        color: var(--lblue);
        font-size: 13px;
        padding: 0 10px;
      }
      &__selected {
        color: var(--font);
        font-size: 16px;
        height: 42px;
        font-weight: normal;
        display: flex;
        align-items: center;
        img {
          margin-right: 10px;
        }
      }
      &__clear {
        display: none;
      }
      &__dropdown-option {
        padding: 5px 10px;
        color: var(--font);
        font-weight: normal;
        display: flex;
        align-items: center;
        img {
          background: var(--white);
          margin-right: 10px;
        }
        &.vs__dropdown-option--highlight {
          background: var(--blue);
          color: var(--white);
        }
      }
      &__dropdown-menu {
        top: calc(100% + 4px);
        border-radius: 4px;
        border: 1px solid var(--lblue);
        box-shadow: none;
      }
    }
  }
}
</style>
